import {
  NotificationAck,
  NotificationAckType,
  NotificationsClientEventName,
  NotificationsRealtimeMode,
  NotificationsServerEventName,
} from '../types/Notifications/types';
import { getApiBaseUrl } from '../axios/Utils/constants';
import { resolveBackendNotificationId } from './notificationHelpers';

type NotificationsServerEvents = {
  notification: NotificationsServerEventName;
  syncResult: NotificationsServerEventName;
};

type NotificationsClientEvents = Record<'sync' | NotificationAckType, NotificationsClientEventName>;

export const NOTIFICATIONS_SERVER_EVENTS: NotificationsServerEvents = {
  notification: 'notification:new',
  syncResult: 'notifications:pending',
};

export const NOTIFICATIONS_CLIENT_EVENTS: NotificationsClientEvents = {
  sync: 'notifications:sync',
  received: 'notifications:received',
  opened: 'notifications:opened',
  seen: 'notifications:seen',
};

const NOTIFICATIONS_REALTIME_DEFAULT_MODE: NotificationsRealtimeMode = 'websocket';
const NOTIFICATIONS_VERBOSE_LOGS_KEY = 'FP_NOTIFICATIONS_VERBOSE_LOGS';

export const getNotificationsRealtimeMode = (): NotificationsRealtimeMode =>
  NOTIFICATIONS_REALTIME_DEFAULT_MODE;

export const getNotificationsRealtimeUrl = (): string | null => {
  const explicitUrl = process.env.REACT_APP_NOTIFICATIONS_WS_URL?.trim();
  if (explicitUrl) return explicitUrl;

  const apiBaseUrl = getApiBaseUrl();
  return apiBaseUrl?.replace(/\/$/, '') || null;
};

export const getNotificationsRealtimePath = (): string =>
  process.env.REACT_APP_NOTIFICATIONS_WS_PATH?.trim() || '/socket.io';

export const isNotificationsRealtimeDebugEnabled = (): boolean =>
  process.env.REACT_APP_NOTIFICATIONS_DEBUG === 'true';

const isNotificationsRealtimeVerboseEnabled = (): boolean => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_NOTIFICATIONS_VERBOSE_LOGS === 'true';
  }

  const storedOverride = window.localStorage.getItem(NOTIFICATIONS_VERBOSE_LOGS_KEY);
  if (storedOverride === 'true') return true;
  if (storedOverride === 'false') return false;

  return process.env.REACT_APP_NOTIFICATIONS_VERBOSE_LOGS === 'true';
};

const NOTIFICATIONS_NOISY_FRAGMENTS = [
  'Conectando socket.io de notificaciones',
  'Websocket de notificaciones conectado',
  'Websocket de notificaciones desconectado',
  'Enviando sync de notificaciones',
  'Resultado de sync recibido',
  'Token recuperado fallback',
];

const shouldSuppressNotificationsConsoleLog = (message: string): boolean => {
  if (isNotificationsRealtimeVerboseEnabled()) return false;
  return NOTIFICATIONS_NOISY_FRAGMENTS.some((fragment) => message.includes(fragment));
};

export const debugNotificationsLog = (message: string, payload?: unknown): void => {
  if (!isNotificationsRealtimeDebugEnabled()) return;
  if (shouldSuppressNotificationsConsoleLog(message)) return;

  if (payload === undefined) {
    console.log(`[Notifications] ${message}`);
    return;
  }

  console.log(`[Notifications] ${message}`, payload);
};

export const buildNotificationsSyncPayload = (cursor?: string | null): { cursor: string | null } =>
  ({ cursor: cursor ?? null });

export const buildNotificationsAckPayload = (ack: NotificationAck): { id: string | number } => ({
  id: resolveBackendNotificationId(ack.id),
});
